/**
 * Role helpers for the HR module (Phase 0).
 *
 * Until now the app had NO route-level authorisation: ProtectedRoute checks only
 * that a token exists, so any authenticated user who types a URL reaches any
 * screen. Access control was purely a matter of what the server-rendered sidebar
 * chose to show. That is not good enough for salary data.
 *
 * These helpers read the roles the login response already stores in
 * localStorage("user_Detail"). They are a convenience for the UI only —
 * the real gate is Helpers::requireRole() on the PHP side, because anything
 * in localStorage is trivially editable by the user.
 */

/** Roles that may operate the HR module (maker: define structures, run payroll, credit leave). */
export const HR_ROLES = ["HR", "Head", "Admin"];

/** Roles that may approve (checker: approve salary structures and payroll runs). */
export const HR_APPROVER_ROLES = ["Head", "Admin"];

/**
 * Role names for the logged-in user.
 *
 * `user_Detail.roles` comes from Helpers::arrayKeysToObjV2, which yields an
 * OBJECT keyed by role name whose values carry { id, name } — not an array.
 * Different screens in this codebase treat it both ways, so normalise here
 * rather than guessing at each call site.
 */
export function getUserRoles(): string[] {
    try {
        const raw = localStorage.getItem("user_Detail");
        if (!raw) return [];

        const detail = JSON.parse(raw);
        if (!detail || !detail.roles) return [];

        return Object.values(detail.roles)
            .map((role: any) => {
                if (typeof role === "string") return role;
                return role?.id ?? role?.name ?? "";
            })
            .filter((name: string) => name !== "");
    } catch (e) {
        // A corrupt user_Detail must not crash the app — treat it as "no roles",
        // which fails closed.
        return [];
    }
}

/** True when the user holds at least one of `roles`. Comparison is case-insensitive. */
export function hasAnyRole(roles: string[]): boolean {
    if (!roles || roles.length === 0) return true;

    const held = getUserRoles().map((r) => r.toLowerCase());
    return roles.some((r) => held.includes(r.toLowerCase()));
}

/** True when the user may operate HR screens. */
export function isHrUser(): boolean {
    return hasAnyRole(HR_ROLES);
}

/** True when the user may approve HR records they did not create. */
export function isHrApprover(): boolean {
    return hasAnyRole(HR_APPROVER_ROLES);
}

/**
 * Hook form, for conditional rendering inside a component.
 *
 * Roles are read from localStorage and only change on login, so there is no
 * subscription to set up — this is a plain read wrapped for readability at
 * call sites: `const canApprove = useHasRole(HR_APPROVER_ROLES);`
 */
export function useHasRole(roles: string[]): boolean {
    return hasAnyRole(roles);
}

/**
 * The logged-in user's own user_profile_id.
 *
 * Needed for maker-cannot-approve-their-own-record checks in the UI. The server
 * repeats this check against the JWT — never trust this value for authorisation.
 */
export function getCurrentUserProfileId(): number | null {
    try {
        const raw = localStorage.getItem("user_Detail");
        if (!raw) return null;

        const detail = JSON.parse(raw);
        const id = detail?.user_profile_id ?? detail?.userProfile?.user_profile_id;

        return id === undefined || id === null ? null : Number(id);
    } catch (e) {
        return null;
    }
}
